import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { escapeHtml, sanitizeHtml } from 'app/core/utils';
import { decodeEscapedContent } from './decode-uri.pipe';

/**
 * Renders chat message text with any URL in it turned into a clickable anchor.
 *
 * The message body is plain text authored by a user, so it is escaped before a single anchor is
 * added. Escaping first is what keeps a message that happens to contain markup from being rendered
 * as markup once the result reaches `[innerHTML]`.
 *
 * `sanitizeHtml` then re-checks the result independently, on the same reasoning the release-note
 * renderer uses: a mistake in the escaping above should not be able to become a scripting bug on
 * its own.
 *
 * Decoding matches the `decodeURI` pipe this replaces, so message bodies read the same as before
 * apart from the links.
 */
const ALLOWED_TAGS: string[] = ['a'];

const ALLOWED_ATTR: string[] = ['href', 'target', 'rel'];

/**
 * Matches an explicit `http(s)://` URL, or a bare `www.` host of the kind people type without a
 * scheme. `<>"'` end a match so that a URL sitting inside quotes or angle brackets does not swallow
 * them.
 */
const URL_PATTERN: RegExp = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

/** Sentence punctuation that follows a URL far more often than it belongs to one. */
const TRAILING_PUNCTUATION: RegExp = /[.,;:!?'"]+$/;

@Pipe({ name: 'linkify', standalone: true })
export class LinkifyPipe implements PipeTransform {
  private readonly _sanitizer = inject(DomSanitizer);

  transform(content: string | null | undefined): SafeHtml {
    const text: string = decodeEscapedContent(content);
    if (text === '') {
      return this._sanitizer.bypassSecurityTrustHtml('');
    }

    return this._sanitizer.bypassSecurityTrustHtml(sanitizeHtml(linkify(text), ALLOWED_TAGS, ALLOWED_ATTR));
  }
}

function linkify(text: string): string {
  let html = '';
  let lastIndex = 0;

  URL_PATTERN.lastIndex = 0;
  for (let match: RegExpExecArray | null = URL_PATTERN.exec(text); match !== null; match = URL_PATTERN.exec(text)) {
    const url: string = trimTrailingPunctuation(match[0]);

    // Punctuation trimming can leave a bare `www.` or scheme behind, which is not worth linking.
    if (!hasHost(url)) {
      continue;
    }

    html += escapeHtml(text.slice(lastIndex, match.index));
    html += `<a href="${escapeHtml(toAbsoluteUrl(url))}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`;

    lastIndex = match.index + url.length;
    // The trimmed punctuation belongs to the sentence, so scanning resumes at it rather than past it.
    URL_PATTERN.lastIndex = lastIndex;
  }

  return html + escapeHtml(text.slice(lastIndex));
}

/**
 * Drops trailing sentence punctuation, and any closing bracket the URL never opened — `(see
 * https://example.com/a)` links `https://example.com/a`, while a URL whose own path is parenthesised
 * keeps them.
 */
function trimTrailingPunctuation(url: string): string {
  let trimmed: string = url.replace(TRAILING_PUNCTUATION, '');

  while (isUnbalancedClosing(trimmed)) {
    trimmed = trimmed.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
  }

  return trimmed;
}

function isUnbalancedClosing(url: string): boolean {
  const closing: string = url.slice(-1);
  const opening: string | null = closing === ')' ? '(' : closing === ']' ? '[' : null;

  return opening !== null && count(url, opening) < count(url, closing);
}

function count(value: string, character: string): number {
  let total = 0;
  for (const current of value) {
    if (current === character) {
      total++;
    }
  }

  return total;
}

function hasHost(url: string): boolean {
  return /^(?:https?:\/\/|www\.)\S/i.test(url);
}

function toAbsoluteUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
